import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { PendingVenue, RejectVenueInput } from "./types";
import { PENDING_VENUES_QUERY_KEY } from "./use-pending-venues";

interface RejectVenueParams {
  readonly venueId: string;
  readonly input: RejectVenueInput;
}

async function rejectVenue({ venueId, input }: RejectVenueParams): Promise<unknown> {
  return httpClient(`/api/admin/venues/${venueId}/reject`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useRejectVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectVenue,
    onMutate: async ({ venueId }) => {
      await queryClient.cancelQueries({ queryKey: PENDING_VENUES_QUERY_KEY });

      const previous = queryClient.getQueryData<readonly PendingVenue[]>(PENDING_VENUES_QUERY_KEY);

      // See comment in use-approve-venue.ts — undefined updater return
      // is a TanStack Query v5 no-op, which is the correct behavior.
      queryClient.setQueryData<readonly PendingVenue[]>(PENDING_VENUES_QUERY_KEY, (old) =>
        old?.filter((v) => v.id !== venueId),
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PENDING_VENUES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PENDING_VENUES_QUERY_KEY });
    },
  });
}
